import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageStates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAppStore } from "@/lib/app-store";
import type { Role } from "@/lib/mock-data";

export const Route = createFileRoute("/app/me")({
  component: Me,
});

const roleText: Record<Role, string> = {
  admin: "管理员",
  editor: "记录者",
  viewer: "观察者",
};

function Me() {
  const navigate = useNavigate();
  const { user, setUser, role, setRole } = useAppStore();
  if (!user) return null;

  return (
    <div className="pb-6">
      <PageHeader title="我的" />
      <section className="mx-4 mt-4 rounded-[var(--r-lg)] border bg-surface p-4">
        <div className="flex items-center gap-3">
          <div
            className="grid h-14 w-14 shrink-0 place-items-center rounded-full text-[var(--fs-lg)] font-semibold"
            style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
          >
            {user.nickname.slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate font-semibold">{user.nickname}</p>
              <span
                className="shrink-0 rounded-full px-2 py-0.5 text-[var(--fs-xs)]"
                style={{ background: "var(--surface-2)", color: "var(--muted)" }}
              >
                {roleText[role]}
              </span>
            </div>
            <p
              className="truncate text-[var(--fs-sm)]"
              style={{ color: "var(--muted)" }}
            >
              {user.email}
            </p>
          </div>
        </div>
      </section>

      <section className="mx-4 mt-4 space-y-3 rounded-[var(--r-lg)] border bg-surface p-4">
        <div>
          <Label className="mb-1 text-[var(--fs-xs)]">昵称</Label>
          <Input
            value={user.nickname}
            onChange={(e) => setUser({ ...user, nickname: e.target.value })}
          />
        </div>
        <div>
          <Label className="mb-1 text-[var(--fs-xs)]">角色(演示切换)</Label>
          <Select value={role} onValueChange={(v) => setRole(v as Role)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">管理员</SelectItem>
              <SelectItem value="editor">记录者</SelectItem>
              <SelectItem value="viewer">观察者</SelectItem>
            </SelectContent>
          </Select>
          <p
            className="mt-1 text-[var(--fs-xs)]"
            style={{ color: "var(--muted)" }}
          >
            切换后返回首页可看到相应权限差异
          </p>
        </div>
      </section>

      <section className="mx-4 mt-4 space-y-2 rounded-[var(--r-lg)] border bg-surface p-2">
        <Row
          label="修改密码"
          onClick={() => navigate({ to: "/reset-password" })}
        />
        <Row
          label="退出登录"
          danger
          onClick={() => {
            toast("已退出登录");
            navigate({ to: "/" });
          }}
        />
      </section>
    </div>
  );
}

function Row({
  label,
  onClick,
  danger,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-[var(--r-sm)] px-3 py-3 text-left text-[var(--fs-sm)] hover:bg-[var(--surface-2)]"
      style={danger ? { color: "var(--danger)" } : undefined}
    >
      <span>{label}</span>
      <span style={{ color: "var(--muted)" }}>›</span>
    </button>
  );
}
